import { useState } from "react";

const inputCls = "border border-gray-300 rounded px-4 py-2 w-full";

export default function ApplicationForm({ jobTitle }) {
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const formEl = e.currentTarget;
    const data = new FormData(formEl);
    data.set("jobTitle", jobTitle);

    // Client-side size check so the user hears before uploading
    for (const key of ["cv", "coverLetter"]) {
      const f = data.get(key);
      if (f && f.size > 2 * 1024 * 1024) {
        setError(`${key === "cv" ? "CV" : "Covering letter"} must be under 2 MB.`);
        return;
      }
    }

    setStatus("sending");
    try {
      const res = await fetch("/api/apply", { method: "POST", body: data });
      const result = await res.json();
      if (result.success) {
        setStatus("sent");
        formEl.reset();
      } else {
        setStatus("error");
        setError(result.error || "Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setError("Could not reach the server. Please try again.");
    }
  };

  if (status === "sent") {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-green-800">
        <p className="font-semibold">Thank you — your application has been sent.</p>
        <p className="text-sm mt-1">We&apos;ll be in touch if you&apos;re shortlisted.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" encType="multipart/form-data">
      {/* 🪤 Honeypot — hidden from humans */}
      <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input type="text" name="name" required placeholder="Full name *" className={inputCls} />
        <input type="email" name="email" required placeholder="Email address *" className={inputCls} />
      </div>

      <input type="tel" name="phone" placeholder="Phone number" className={inputCls} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Upload CV * <span className="text-gray-400">(PDF/Word, max 2 MB)</span></span>
          <input type="file" name="cv" required accept=".pdf,.doc,.docx" className="mt-1 block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-yellow-100 file:text-yellow-800 hover:file:bg-yellow-200" />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Upload covering letter * <span className="text-gray-400">(PDF/Word, max 2 MB)</span></span>
          <input type="file" name="coverLetter" required accept=".pdf,.doc,.docx" className="mt-1 block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-yellow-100 file:text-yellow-800 hover:file:bg-yellow-200" />
        </label>
      </div>

      <textarea name="message" rows="3" placeholder="Anything you'd like to add (optional)" className={inputCls} />

      {error && <p className="text-red-600 text-sm">{error}</p>}

      <button
        type="submit"
        disabled={status === "sending"}
        className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-full font-semibold hover:bg-gray-800 transition disabled:opacity-60"
      >
        {status === "sending" ? "Sending…" : "Submit application"}
      </button>

      <p className="text-xs text-gray-500">
        Your details and documents are sent directly to SRK Trends Ltd for this vacancy only and are not stored on this website.
      </p>
    </form>
  );
}